import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Tabs,
    Tab,
    Paper,
    Stack,
    Button,
    TextField,
    InputAdornment,
    MenuItem as MuiMenuItem,
    Typography,
    Chip
} from '@mui/material';
import { Search, PlaylistAdd, Timeline, Info, Edit, Delete } from '@mui/icons-material';
import feedbackService from '../services/feedbackService';
import { useAuth } from '../context/AuthContext';
import RequestCard from '../components/feedback/RequestCard';
import SubmitRequestForm from '../components/feedback/SubmitRequestForm';
import ModalScaffold from '../components/common/ModalScaffold';
import Sparkline from '../components/feedback/Sparkline';
import { MenuItem, Select, FormControl, InputLabel } from '@mui/material';

function a11yProps(index) {
    return {
        id: `feedback-tab-${index}`,
        'aria-controls': `feedback-tabpanel-${index}`,
    };
}

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`feedback-tabpanel-${index}`}
            aria-labelledby={`feedback-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 2 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function BrowseTab() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [items, setItems] = useState([]);
    const [detail, setDetail] = useState({ open: false, id: null, item: null });
    const [edit, setEdit] = useState({ open: false, item: null, title: '', description: '' });
    const [confirm, setConfirm] = useState({ open: false, id: null });
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [category, setCategory] = useState('');
    const [sort, setSort] = useState('trending');

    const fetch = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await feedbackService.list({ search, status, category, sort });
            setItems(res.data?.data || []);
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || 'Failed to load feedback');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!detail.open || !detail.id) return;
        (async () => {
            try {
                const res = await feedbackService.get(detail.id);
                setDetail((d) => ({ ...d, item: res.data?.data }));
            } catch (_) {}
        })();
    }, [detail.open, detail.id]);

    return (
        <>
            <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                        placeholder="Search feedback"
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                        sx={{ flex: 1 }}
                    />
                    <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 140 }}>
                        <MuiMenuItem value="">All</MuiMenuItem>
                        <MuiMenuItem value="under_review">Under Review</MuiMenuItem>
                        <MuiMenuItem value="planned">Planned</MuiMenuItem>
                        <MuiMenuItem value="in_progress">In Progress</MuiMenuItem>
                        <MuiMenuItem value="released">Released</MuiMenuItem>
                    </TextField>
                    <TextField select size="small" label="Category" value={category} onChange={(e) => setCategory(e.target.value)} sx={{ minWidth: 140 }}>
                        <MuiMenuItem value="">All</MuiMenuItem>
                        <MuiMenuItem value="ui">UI</MuiMenuItem>
                        <MuiMenuItem value="reporting">Reporting</MuiMenuItem>
                        <MuiMenuItem value="integrations">Integrations</MuiMenuItem>
                    </TextField>
                    <TextField select size="small" label="Sort" value={sort} onChange={(e) => setSort(e.target.value)} sx={{ minWidth: 140 }}>
                        <MuiMenuItem value="trending">Trending</MuiMenuItem>
                        <MuiMenuItem value="top">Most Voted</MuiMenuItem>
                        <MuiMenuItem value="new">Newest</MuiMenuItem>
                    </TextField>
                    <Button variant="outlined" size="small" onClick={fetch} disabled={loading}>Apply</Button>
                </Stack>

                <Stack spacing={1}>
                    {!!error && (
                        <Typography variant="body2" color="error">{error}</Typography>
                    )}
                    {items.map((req) => (
                        <RequestCard
                            key={req._id}
                            req={req}
                            onVote={async () => {
                                try {
                                    const res = await feedbackService.toggleVote(req._id);
                                    setItems((prev) => prev.map((i) => i._id === req._id ? { ...i, hasVoted: res.data?.data?.hasVoted, voteCount: res.data?.data?.voteCount } : i));
                                } catch (_) {}
                            }}
                            onOpen={() => setDetail({ open: true, id: req._id })}
                            showOwnerActions={req.status === 'under_review' && req.isOwner}
                            onEdit={() => setEdit({ open: true, item: req, title: req.title, description: req.description })}
                            onDelete={() => setConfirm({ open: true, id: req._id })}
                        />
                    ))}
                    {!loading && items.length === 0 && (
                        <Typography variant="body2" color="text.secondary">No feedback yet.</Typography>
                    )}
                </Stack>
            </Stack>

            {/* Detail Modal */}
            <ModalScaffold
                open={detail.open}
                onClose={() => setDetail({ open: false, id: null, item: null })}
                maxWidth="sm"
                title={detail.item?.title || 'Feedback'}
                subtitle={detail.item ? `${detail.item.category || 'uncategorized'} • ${String(detail.item.status || '').replace(/_/g, ' ')}` : ''}
                icon={Info}
                actions={<Button onClick={() => setDetail({ open: false, id: null, item: null })}>Close</Button>}
            >
                <Stack spacing={2}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{detail.item?.description}</Typography>
                    {(user?.role === 'admin' || user?.role === 'company_admin') && detail.item && (
                        <AdminStatusEditor
                            request={detail.item}
                            onSaved={(updated) => {
                                setDetail((d) => ({ ...d, item: updated }));
                                setItems((prev) => prev.map((i) => i._id === updated._id ? { ...i, status: updated.status, category: updated.category } : i));
                            }}
                        />
                    )}
                </Stack>
            </ModalScaffold>

            {/* Edit Modal */}
            <ModalScaffold
                open={edit.open}
                onClose={() => setEdit({ open: false, item: null, title: '', description: '' })}
                maxWidth="sm"
                title="Edit Feedback"
                icon={Edit}
                actions={
                    <>
                        <Button onClick={() => setEdit({ open: false, item: null, title: '', description: '' })}>Cancel</Button>
                        <Button variant="contained" onClick={async () => {
                            try {
                                const res = await feedbackService.ownerEdit(edit.item._id, { title: edit.title, description: edit.description, category: edit.item.category });
                                const updated = res.data?.data;
                                setItems((prev) => prev.map((i) => i._id === edit.item._id ? { ...i, title: updated.title, description: updated.description, category: updated.category } : i));
                                setEdit({ open: false, item: null, title: '', description: '' });
                            } catch (_) {}
                        }}>Save</Button>
                    </>
                }
            >
                <Stack spacing={2}>
                    <TextField label="Title" size="small" value={edit.title} onChange={(e) => setEdit((s) => ({ ...s, title: e.target.value }))} />
                    <TextField label="Description" multiline minRows={4} value={edit.description} onChange={(e) => setEdit((s) => ({ ...s, description: e.target.value }))} />
                </Stack>
            </ModalScaffold>

            {/* Delete Confirm Modal */}
            <ModalScaffold
                open={confirm.open}
                onClose={() => setConfirm({ open: false, id: null })}
                maxWidth="xs"
                title="Delete Feedback"
                icon={Delete}
                actions={
                    <>
                        <Button onClick={() => setConfirm({ open: false, id: null })}>Cancel</Button>
                        <Button color="error" variant="contained" onClick={async () => {
                            try {
                                await feedbackService.remove(confirm.id);
                                setItems((prev) => prev.filter((i) => i._id !== confirm.id));
                                setConfirm({ open: false, id: null });
                            } catch (_) {}
                        }}>Delete</Button>
                    </>
                }
            >
                <Typography variant="body2">Are you sure you want to delete this request? This action cannot be undone.</Typography>
            </ModalScaffold>
        </>
    );
}

function AdminStatusEditor({ request, onSaved }) {
    const [status, setStatus] = useState(request.status || 'under_review');
    const [saving, setSaving] = useState(false);
    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await feedbackService.update(request._id, { status });
            onSaved?.(res.data?.data || { ...request, status });
        } finally {
            setSaving(false);
        }
    };
    return (
        <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="status-label">Status</InputLabel>
                <Select labelId="status-label" label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <MenuItem value="under_review">Under Review</MenuItem>
                    <MenuItem value="planned">Planned</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="released">Released</MenuItem>
                    <MenuItem value="declined">Declined</MenuItem>
                </Select>
            </FormControl>
            <Button variant="contained" onClick={handleSave} disabled={saving}>Save</Button>
        </Stack>
    );
}

function SubmitTab() {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);
    const handleSubmit = async (data) => {
        setSubmitting(true);
        try {
            await feedbackService.create(data);
            setSuccessOpen(true);
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <>
            <SubmitRequestForm onSubmit={handleSubmit} submitting={submitting} />
            <ModalScaffold
                open={successOpen}
                onClose={() => { setSuccessOpen(false); navigate('/feedback'); }}
                maxWidth="xs"
                title="Request Submitted"
                actions={<Button onClick={() => { setSuccessOpen(false); navigate('/feedback'); }}>OK</Button>}
            >
                <Typography variant="body2">Thanks! Your feedback has been submitted.</Typography>
            </ModalScaffold>
        </>
    );
}

function RoadmapTab() {
    const [data, setData] = useState({ under_review: [], planned: [], in_progress: [], released: [] });
    const load = async () => {
        const res = await feedbackService.list({});
        const items = res.data?.data || [];
        const grouped = { under_review: [], planned: [], in_progress: [], released: [] };
        items.forEach((i) => { (grouped[i.status] || grouped.under_review).push(i); });
        setData(grouped);
    };
    useEffect(() => { load(); }, []);
    const [detail, setDetail] = useState({ open: false, id: null, item: null });
    useEffect(() => {
        if (!detail.open || !detail.id) return;
        (async () => {
            try {
                const res = await feedbackService.get(detail.id);
                setDetail((d) => ({ ...d, item: res.data?.data }));
            } catch (_) {}
        })();
    }, [detail.open, detail.id]);

    const Column = ({ title, items }) => (
        <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
            <Stack spacing={1}>
                {items.map((i) => (
                    <Paper key={i._id} variant="outlined" sx={{ p: 1.5, cursor: 'pointer' }} onClick={() => setDetail({ open: true, id: i._id })}>
                        <Typography variant="body2" fontWeight={600}>{i.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{i.voteCount ?? 0} votes</Typography>
                    </Paper>
                ))}
            </Stack>
        </Box>
    );
    return (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Column title="Under Review" items={data.under_review} />
            <Column title="Planned" items={data.planned} />
            <Column title="In Progress" items={data.in_progress} />
            <Column title="Released" items={data.released} />
            <ModalScaffold
                open={detail.open}
                onClose={() => setDetail({ open: false, id: null, item: null })}
                maxWidth="sm"
                title={detail.item?.title || 'Feedback'}
                subtitle={detail.item ? `${detail.item.category || 'uncategorized'} • ${String(detail.item.status || '').replace(/_/g, ' ')}` : ''}
                icon={Info}
                actions={<Button onClick={() => setDetail({ open: false, id: null, item: null })}>Close</Button>}
            >
                <Stack spacing={1.5}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{detail.item?.description}</Typography>
                    <Typography variant="caption" color="text.secondary">{detail.item?.voteCount ?? 0} votes</Typography>
                </Stack>
            </ModalScaffold>
        </Stack>
    );
}

function FeedbackPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [stats, setStats] = useState({ byStatus: {}, topCategories: [], totalVotes: 0 });
    const initialTab = useMemo(() => {
        const p = location.pathname;
        if (p.includes('/submit')) return 1;
        if (p.includes('/roadmap')) return 2;
        return 0;
    }, [location.pathname]);
    const [tab, setTab] = useState(initialTab);

    useEffect(() => {
        // Keep tab in sync if the URL changes after mount
        // Only update when it differs to avoid extra renders
        if (initialTab !== tab) setTab(initialTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialTab]);

    useEffect(() => {
        (async () => {
            try {
                const res = await feedbackService.stats();
                setStats(res.data?.data || { byStatus: {}, topCategories: [], totalVotes: 0 });
            } catch (_) {}
        })();
    }, []);

    const handleTabChange = (e, v) => {
        setTab(v);
        if (v === 0) navigate('/feedback');
        if (v === 1) navigate('/feedback/submit');
        if (v === 2) navigate('/feedback/roadmap');
    };

    return (
        <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2, gap: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6">Feedback</Typography>
                    <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
                        <Chip size="small" color="default" label={`Under Review: ${stats.byStatus.under_review || 0}`} />
                        <Chip size="small" color="info" label={`Planned: ${stats.byStatus.planned || 0}`} />
                        <Chip size="small" color="warning" label={`In Progress: ${stats.byStatus.in_progress || 0}`} />
                        <Chip size="small" color="success" label={`Released: ${stats.byStatus.released || 0}`} />
                    </Stack>
                </Stack>
                <Button variant="contained" startIcon={<PlaylistAdd />} onClick={() => navigate('/feedback/submit')}>New Request</Button>
            </Stack>
            {/* Segmented bar for status distribution + sparkline */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                {(() => {
                    const u = stats.byStatus.under_review || 0;
                    const p = stats.byStatus.planned || 0;
                    const i = stats.byStatus.in_progress || 0;
                    const r = stats.byStatus.released || 0;
                    const total = u + p + i + r || 1;
                    const seg = (n) => `${(n / total) * 100}%`;
                    return (
                        <Box sx={{ display: 'flex', height: 10, minWidth: 220, borderRadius: 999, overflow: 'hidden', boxShadow: (t) => t.shadows[1] }}>
                            <Box sx={{ width: seg(u), bgcolor: 'grey.400' }} />
                            <Box sx={{ width: seg(p), bgcolor: 'info.light' }} />
                            <Box sx={{ width: seg(i), bgcolor: 'warning.light' }} />
                            <Box sx={{ width: seg(r), bgcolor: 'success.light' }} />
                        </Box>
                    );
                })()}
                <Sparkline data={stats.trend || []} />
                {Boolean((stats.topCategories || []).length) && (
                    <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
                        {(stats.topCategories || []).map((c) => {
                            const name = !c.category || String(c.category).trim() === '' ? 'uncategorized' : c.category;
                            return (
                                <Chip key={name} size="small" variant="outlined" label={`${name}: ${c.count}`} />
                            );
                        })}
                    </Stack>
                )}
            </Box>
            <Paper sx={{ p: 2 }}>
                <Tabs value={tab} onChange={handleTabChange} variant="scrollable" scrollButtons allowScrollButtonsMobile>
                    <Tab label="Browse" {...a11yProps(0)} />
                    <Tab label="Submit" {...a11yProps(1)} />
                    <Tab icon={<Timeline />} iconPosition="start" label="Roadmap" {...a11yProps(2)} />
                </Tabs>
                <TabPanel value={tab} index={0} data-usage-screen="feedback.browse">
                    {tab === 0 && <BrowseTab />}
                </TabPanel>
                <TabPanel value={tab} index={1} data-usage-screen="feedback.submit">
                    {tab === 1 && <SubmitTab />}
                </TabPanel>
                <TabPanel value={tab} index={2} data-usage-screen="feedback.roadmap">
                    {tab === 2 && <RoadmapTab />}
                </TabPanel>
            </Paper>
        </Box>
    );
}

export default FeedbackPage;


